import {
  deleteV1CompaniesCompanyIdVetaCenterConnection,
  deleteV1CompaniesCompanyIdVetaCenterProjectLinksId,
  patchV1CompaniesCompanyIdVetaCenterConnection,
  postV1CompaniesCompanyIdVetaCenterConnection,
  postV1CompaniesCompanyIdVetaCenterConnectionRefreshContracts,
  postV1CompaniesCompanyIdVetaCenterConnectionRegister,
  postV1CompaniesCompanyIdVetaCenterProjectLinks,
  postV1CompaniesCompanyIdVetaCenterTestTickets,
  putV1CompaniesCompanyIdVetaCenterReferenceKey,
  VetaCenterEnvironment,
  VetaCenterReferenceKeyMatchableType,
  VetaCenterReferenceKeyQualifier,
} from '@treadinc/horizon-api-spec';
import { isAxiosError } from 'axios';
import { t } from 'i18next';
import { useCallback } from 'react';

import {
  NOT_FOUND,
  SERVICE_UNAVAILABLE,
  UNPROCESSABLE_ENTITY,
} from '~constants/errorMessagesConsts';
import { readFirstError } from '~queries/utils';
import connection from '~services/connectionModule';

import { VetaCenterConnection, VetaCenterProjectLink } from './models';

interface VetaCenterConnectionCreateInput {
  environment: VetaCenterEnvironment;
  servicePassword: string;
  serviceUser: string;
}

interface VetaCenterProjectLinkCreateInput {
  externalContractGuid: string;
  externalWorkspaceGuid: string;
  projectId: string;
}

interface VetaCenterReferenceKeyInput {
  matchableId: string;
  matchableType: VetaCenterReferenceKeyMatchableType;
  qualifier: VetaCenterReferenceKeyQualifier;
  value: string;
}

export function readReferenceKeyRejection(error: unknown) {
  return (
    readFirstError(error)?.message ??
    t('error_messages.veta_center.reference_key_rejected')
  );
}

function notify(error: unknown, message: string) {
  try {
    connection.handleRequestError(error, message);
  } catch {
    // Error handling side effects already executed in connection.handleRequestError.
  }
}

export default function useVetaCenter() {
  const createConnection = useCallback(
    async (companyId: string, input: VetaCenterConnectionCreateInput) => {
      try {
        const response = await postV1CompaniesCompanyIdVetaCenterConnection({
          body: {
            environment: input.environment,
            service_password: input.servicePassword,
            service_user: input.serviceUser,
          },
          path: { 'company-id': companyId },
        });

        return VetaCenterConnection.parse(response.data.data);
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === UNPROCESSABLE_ENTITY) {
          throw error;
        }

        notify(
          error,
          isAxiosError(error) && error.response?.status === SERVICE_UNAVAILABLE
            ? t('error_messages.veta_center.unreachable')
            : t('error_messages.veta_center.failed_to_create_connection'),
        );

        return null;
      }
    },
    [],
  );

  const deleteConnection = useCallback(async (companyId: string) => {
    try {
      await deleteV1CompaniesCompanyIdVetaCenterConnection({
        path: { 'company-id': companyId },
      });

      return true;
    } catch (error) {
      notify(error, t('error_messages.veta_center.failed_to_delete_connection'));

      return false;
    }
  }, []);

  const updateOwnFleetHaulerCompanyGuid = useCallback(
    async (companyId: string, guid: string | null) => {
      try {
        const response = await patchV1CompaniesCompanyIdVetaCenterConnection({
          body: { external_own_fleet_hauler_company_guid: guid },
          path: { 'company-id': companyId },
        });

        return VetaCenterConnection.parse(response.data.data);
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === UNPROCESSABLE_ENTITY) {
          throw error;
        }

        notify(error, t('error_messages.veta_center.failed_to_update_connection'));

        return null;
      }
    },
    [],
  );

  const refreshContracts = useCallback(async (companyId: string) => {
    try {
      await postV1CompaniesCompanyIdVetaCenterConnectionRefreshContracts({
        path: { 'company-id': companyId },
      });

      return true;
    } catch (error) {
      notify(error, t('error_messages.veta_center.failed_to_refresh_contracts'));

      return false;
    }
  }, []);

  const createProjectLink = useCallback(
    async (companyId: string, input: VetaCenterProjectLinkCreateInput) => {
      try {
        const response = await postV1CompaniesCompanyIdVetaCenterProjectLinks({
          body: {
            external_contract_guid: input.externalContractGuid,
            external_workspace_guid: input.externalWorkspaceGuid,
            project_id: input.projectId,
          },
          path: { 'company-id': companyId },
        });

        return VetaCenterProjectLink.parse(response.data.data);
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === UNPROCESSABLE_ENTITY) {
          throw error;
        }

        notify(error, t('error_messages.veta_center.failed_to_create_project_link'));

        return null;
      }
    },
    [],
  );

  const deleteProjectLink = useCallback(async (companyId: string, linkId: string) => {
    try {
      await deleteV1CompaniesCompanyIdVetaCenterProjectLinksId({
        path: { 'company-id': companyId, id: linkId },
      });

      return true;
    } catch (error) {
      notify(error, t('error_messages.veta_center.failed_to_delete_project_link'));

      return false;
    }
  }, []);

  const register = useCallback(async (companyId: string) => {
    try {
      await postV1CompaniesCompanyIdVetaCenterConnectionRegister({
        path: { 'company-id': companyId },
      });

      return true;
    } catch (error) {
      notify(error, t('error_messages.veta_center.failed_to_register'));

      return false;
    }
  }, []);

  const setReferenceKey = useCallback(
    async (companyId: string, input: VetaCenterReferenceKeyInput) => {
      try {
        await putV1CompaniesCompanyIdVetaCenterReferenceKey({
          body: {
            matchable_id: input.matchableId,
            matchable_type: input.matchableType,
            qualifier: input.qualifier,
            value: input.value,
          },
          path: { 'company-id': companyId },
        });

        return true;
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === UNPROCESSABLE_ENTITY) {
          throw error;
        }

        notify(error, t('error_messages.veta_center.failed_to_save_reference_key'));

        return false;
      }
    },
    [],
  );

  const sendTestTicket = useCallback(
    async (companyId: string, input: { projectLinkId?: string; siteId: string }) => {
      try {
        await postV1CompaniesCompanyIdVetaCenterTestTickets({
          body: {
            site_id: input.siteId,
            veta_center_project_link_id: input.projectLinkId,
          },
          path: { 'company-id': companyId },
        });

        return true;
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === NOT_FOUND) {
          throw error;
        }

        notify(error, t('error_messages.veta_center.failed_to_send_test_ticket'));

        return false;
      }
    },
    [],
  );

  return {
    createConnection,
    createProjectLink,
    deleteConnection,
    deleteProjectLink,
    refreshContracts,
    register,
    sendTestTicket,
    setReferenceKey,
    updateOwnFleetHaulerCompanyGuid,
  };
}
